import React, { useState } from "react";
import ReactDOM from "react-dom";
import { ConfigProvider, Select } from "antd";

import { showMessage } from "../../../Functionalities/Functions";
import confirmBox from "../../../CoreComponents/confirmBox";
import { getOrderItemsAdmin, updateOrderItems } from "../Logic";
import { groupMenuByProduct, variantPrice } from "./menuGrouping";
import "./EditOrderItems.css";

const THEME = {
  token: {
    colorPrimary: "#fb310d",
    borderRadius: 8,
    fontFamily: "inherit",
    controlHeight: 40,
  },
};

const rowFromMenuEntry = (entry) => ({
  item_varient: entry.item_varient,
  name: entry.item_name + (entry.varient_name ? " - " + entry.varient_name : ""),
  rate: variantPrice(entry),
  qty: 1,
});

const productOption = (product) => ({
  value: product.key,
  name: product.item_name,
  label: (
    <span className="eoi-option">
      <span className="eoi-option__name">{product.item_name}</span>
      <span className="eoi-option__meta">
        {product.variants.length > 1
          ? "· " + product.variants.length + " variants"
          : "· ₹" + variantPrice(product.variants[0])}
      </span>
      {product.is_out_of_stock && <span className="eoi-tag">out of stock</span>}
    </span>
  ),
});

const matchesSearch = (input, option) =>
  option.name.toLowerCase().includes(input.trim().toLowerCase());

function DietMark({ type }) {
  const diet = type || "unspecified";
  return <span className={"eoi-diet eoi-diet--" + diet} role="img" aria-label={diet} />;
}

function Price({ entry }) {
  const price = variantPrice(entry);
  return (
    <span className="eoi-price">
      ₹{price}
      {entry.offer_enabled && entry.rate !== price && (
        <s className="eoi-price__was">₹{entry.rate}</s>
      )}
    </span>
  );
}

const rupees = (value) => Math.round(value * 100) / 100;

export const amountChangeMessage = (data) => {
  const diff = data.amount_difference;
  const totals = "The new total is ₹" + data.new_net_amount + " (was ₹" + data.old_net_amount + "). ";
  if (data.wallet_refund != null) {
    return (
      totals +
      "The customer paid more than that from the wallet, so ₹" +
      data.wallet_refund +
      " will be refunded to the customer's wallet and nothing is collected at the door. Continue?"
    );
  }
  const paid = "This order is already paid online. " + totals;
  if (diff > 0) {
    const fromWallet = data.wallet_debit || 0;
    if (fromWallet <= 0) {
      return paid + "The delivery boy will collect the extra ₹" + diff + " from the customer. Continue?";
    }
    const atTheDoor = rupees(diff - fromWallet);
    return (
      paid +
      "₹" + fromWallet + " will be taken from the customer's wallet" +
      (atTheDoor > 0 ? " and the delivery boy will collect the extra ₹" + atTheDoor + " from the customer" : "") +
      ". Continue?"
    );
  }
  if (data.wallet_credit == null) {
    return paid + "₹" + Math.abs(diff) + " will be refunded to the customer's wallet. Continue?";
  }
  const lessAtTheDoor = rupees(Math.abs(diff) - data.wallet_credit);
  const refund =
    data.wallet_credit > 0
      ? "₹" + data.wallet_credit + " will be refunded to the customer's wallet"
      : "Nothing goes back to the wallet";
  return (
    paid +
    refund +
    (lessAtTheDoor > 0 ? " and the delivery boy will collect ₹" + lessAtTheDoor + " less" : "") +
    ". Continue?"
  );
};

function EditOrderItems({ orderid }) {
  const [rows, setRows] = useState([]);
  const [menu, setMenu] = useState([]);
  // null, not undefined: antd treats an undefined value as uncontrolled and keeps its own selection.
  const [selectedProductKey, setSelectedProductKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const products = groupMenuByProduct(menu);
  const inOrder = new Set(rows.map((row) => row.item_varient));
  const productsWithSomethingToAdd = products.filter((product) =>
    product.variants.some((variant) => !inOrder.has(variant.item_varient))
  );
  const selectedProduct = products.find((product) => product.key === selectedProductKey);

  const loadItems = () => {
    setLoading(true);
    getOrderItemsAdmin(orderid)
      .then((result) => {
        setLoading(false);
        if (result?.data?.Status === true) {
          setMenu(result.data.menu);
          setSelectedProductKey(null);
          setRows(
            result.data.issued_items.map((item) => ({
              item_varient: item.item_varient.id,
              name: item.item.name + " - " + item.item_varient.name,
              rate: item.offer_rate ? item.offer_rate : item.rate,
              qty: item.qty,
            }))
          );
        } else {
          showMessage(
            result?.data?.Error ?? "Error occured",
            "danger",
            "Error!",
            "top",
            "top-right"
          );
        }
      })
      .catch(() => {
        setLoading(false);
        showMessage("Error occured", "danger", "Error!", "top", "top-right");
      });
  };

  const setQty = (index, qty) => {
    setRows(rows.map((row, i) => (i === index ? { ...row, qty } : row)));
  };

  const removeRow = (index) => {
    setRows(rows.filter((_, i) => i !== index));
  };

  const addVariant = (entry) => {
    if (inOrder.has(entry.item_varient)) {
      showMessage(
        "Item is already in the order, change its quantity instead",
        "danger",
        "Error!",
        "top",
        "top-right"
      );
      return null;
    }
    const nextRows = [...rows, rowFromMenuEntry(entry)];
    setRows(nextRows);
    return nextRows;
  };

  const selectProduct = (key) => {
    const product = products.find((p) => p.key === key);
    if (product && product.variants.length === 1) {
      addVariant(product.variants[0]);
      setSelectedProductKey(null);
      return;
    }
    setSelectedProductKey(product ? key : null);
  };

  const addFromVariantList = (entry) => {
    const nextRows = addVariant(entry);
    if (!nextRows) return;
    const added = new Set(nextRows.map((row) => row.item_varient));
    if (selectedProduct.variants.every((variant) => added.has(variant.item_varient))) {
      setSelectedProductKey(null);
    }
  };

  const saveItems = () => {
    if (rows.length === 0) {
      showMessage(
        "An order must keep at least one item; cancel the order instead",
        "danger",
        "Error!",
        "top",
        "top-right"
      );
      return;
    }
    if (rows.some((row) => !Number(row.qty) || Number(row.qty) <= 0)) {
      showMessage(
        "Every item needs a quantity above zero",
        "danger",
        "Error!",
        "top",
        "top-right"
      );
      return;
    }
    submitItems(false);
  };

  const submitItems = (confirmAmountChange) => {
    setSaving(true);
    updateOrderItems(
      orderid,
      rows.map((row) => ({
        item_varient: row.item_varient,
        qty: Number(row.qty),
      })),
      confirmAmountChange
    )
      .then((res) => {
        setSaving(false);
        if (res?.data?.Status === true) {
          showMessage(
            res.data.Message,
            "success",
            "Success!",
            "top",
            "top-right",
            0
          );
          window.location.reload();
        } else if (res?.data?.requires_confirmation === true) {
          confirmBox({
            title: "The order amount changes",
            message: amountChangeMessage(res.data),
            yesLabel: "Continue",
            noLabel: "Cancel",
            onYes: () => submitItems(true),
          });
        } else {
          showMessage(
            res?.data?.Error ?? "Error occured",
            "danger",
            "Error!",
            "top",
            "top-right"
          );
        }
      })
      .catch(() => {
        setSaving(false);
        showMessage("Error occured", "danger", "Error!", "top", "top-right");
      });
  };

  const renderPicker = () => {
    if (products.length === 0) {
      return <p className="text-muted eoi-empty">No items are available for this branch.</p>;
    }
    return (
      <div className="eoi-picker">
        <ConfigProvider theme={THEME}>
          <Select
            showSearch
            allowClear
            value={selectedProductKey}
            placeholder="Search for an item to add"
            options={productsWithSomethingToAdd.map(productOption)}
            optionLabelProp="name"
            filterOption={matchesSearch}
            onChange={selectProduct}
            notFoundContent={<span className="text-muted">No matching item</span>}
            // The Bootstrap modal is its own stacking context (z-index 1050), so a body-level dropdown would sit behind it.
            getPopupContainer={(node) => node.parentElement}
          />
        </ConfigProvider>

        {selectedProduct && (
          <ul className="eoi-variants">
            {selectedProduct.variants.map((entry) => (
              <li key={entry.item_varient} className="eoi-variant">
                <DietMark type={entry.type} />
                <span className="eoi-variant__name">
                  {entry.varient_name || entry.item_name}
                </span>
                <Price entry={entry} />
                {inOrder.has(entry.item_varient) ? (
                  <button type="button" className="btn btn-outline-secondary btn-xs" disabled>
                    In order
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary btn-xs"
                    onClick={() => addFromVariantList(entry)}
                  >
                    Add
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-primary btn-xs"
        data-toggle="modal"
        data-target="#editOrderItemsModal"
        onClick={loadItems}
      >
        Edit Items
      </button>

      {ReactDOM.createPortal(
        <div className="modal fade" id="editOrderItemsModal">
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Order Items</h5>
                <button type="button" className="close" data-dismiss="modal">
                  <span>×</span>
                </button>
              </div>

              <div className="modal-body">
                {loading ? (
                  <p>Loading...</p>
                ) : (
                  <>
                    <table className="table items-table table-responsive-md">
                      <tbody>
                        <tr>
                          <th className="my-0 text-black font-w500">Item</th>
                          <th
                            style={{ width: "160px" }}
                            className="my-0 text-black font-w500"
                          >
                            Qty
                          </th>
                          <th
                            style={{ width: "20%" }}
                            className="my-0 text-black font-w500"
                          >
                            Price
                          </th>
                          <th />
                        </tr>
                        {rows.map((row, index) => (
                          <tr key={row.item_varient}>
                            <td>{row.name}</td>
                            <td>
                              <div className="d-flex align-items-center" style={{ gap: "4px" }}>
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-xs px-2"
                                  style={{ minWidth: "28px", height: "32px" }}
                                  disabled={Number(row.qty) <= 1}
                                  onClick={() =>
                                    setQty(
                                      index,
                                      Math.max(1, Number(row.qty || 1) - 1)
                                    )
                                  }
                                >
                                  -
                                </button>
                                <input
                                  type="number"
                                  min={1}
                                  className="form-control text-center px-1"
                                  style={{ width: "55px", height: "32px" }}
                                  value={row.qty}
                                  onChange={(e) => setQty(index, e.target.value)}
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-primary btn-xs px-2"
                                  style={{ minWidth: "28px", height: "32px" }}
                                  onClick={() =>
                                    setQty(index, Number(row.qty || 0) + 1)
                                  }
                                >
                                  +
                                </button>
                              </div>
                            </td>
                            <td>₹ {row.rate}</td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-danger btn-xs"
                                onClick={() => removeRow(index)}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {renderPicker()}

                    <p style={{ marginTop: 10 }} className="text-muted">
                      GST is worked out again on the new food total. Delivery,
                      tip, wallet and promo stay unchanged. The customer is
                      notified after saving.
                    </p>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-dismiss="modal"
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={saving || loading}
                  onClick={saveItems}
                >
                  {saving ? "Saving..." : "Save Items"}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default EditOrderItems;
